export type SwaggerImportEnvironmentOutcome =
  | 'created'
  | 'reused'
  | 'skipped_plan_limit'
  | 'skipped';

export type SwaggerImportStepStatus = 'pending' | 'running' | 'done';

export type SwaggerImportStats = {
  collections: number;
  requests: number;
  collectionsSkipped?: number;
  requestsSkipped?: number;
  collectionsInSpec?: number;
  requestsInSpec?: number;
  dataSets: number;
  assertions: number;
  environmentOutcome?: SwaggerImportEnvironmentOutcome;
};

export type AgentSwaggerImportStep = {
  id: string;
  status: SwaggerImportStepStatus;
  label: string;
  tone?: 'success' | 'warning';
};

export type SwaggerImportLiveCounts = {
  collectionsCreated?: number;
  requestsCreated?: number;
  dataSetsCreated?: number;
  assertionsCreated?: number;
};

export const SWAGGER_IMPORT_STEP_ADVANCE_MS = 2200;
export const SWAGGER_IMPORT_COMPLETE_PAUSE_MS = 1000;

function formatCollectionsRequestsImportLabel(stats: SwaggerImportStats): string {
  if (stats.collections > 0 || stats.requests > 0) {
    return `Imported ${stats.collections} collection${stats.collections === 1 ? '' : 's'} and ${stats.requests} request${stats.requests === 1 ? '' : 's'}`;
  }
  const skippedCollections = stats.collectionsSkipped ?? 0;
  const skippedRequests = stats.requestsSkipped ?? 0;
  if (skippedCollections > 0 || skippedRequests > 0) {
    return `Matched ${skippedCollections} existing collection${skippedCollections === 1 ? '' : 's'} and ${skippedRequests} request${skippedRequests === 1 ? '' : 's'}`;
  }
  return `Imported ${stats.collections} collection${stats.collections === 1 ? '' : 's'} and ${stats.requests} request${stats.requests === 1 ? '' : 's'}`;
}

function formatDataSetsImportLabel(stats: SwaggerImportStats): string {
  if (stats.dataSets > 0) {
    return `Created ${stats.dataSets} data set${stats.dataSets === 1 ? '' : 's'}`;
  }
  return 'No new data sets created';
}

function formatAssertionsImportLabel(stats: SwaggerImportStats): string {
  if (stats.assertions > 0) {
    return `Created ${stats.assertions} assertion${stats.assertions === 1 ? '' : 's'}`;
  }
  return 'No new assertions created';
}

const STEP_ENVIRONMENT = {
  id: 'environment',
  runningLabel: 'Creating environment…',
  doneLabel: 'Environment created successfully',
  doneWithStats: () => 'Environment created successfully',
} as const;

const STEP_COLLECTIONS = {
  id: 'collections',
  runningLabel: 'Importing collections and requests…',
  doneLabel: 'Collections and requests imported',
  doneWithStats: (stats: SwaggerImportStats) => formatCollectionsRequestsImportLabel(stats),
} as const;

const STEP_DATASETS = {
  id: 'datasets',
  runningLabel: 'Creating data sets…',
  doneLabel: 'Data sets created',
  doneWithStats: (stats: SwaggerImportStats) => formatDataSetsImportLabel(stats),
} as const;

const STEP_ASSERTIONS = {
  id: 'assertions',
  runningLabel: 'Creating assertions…',
  doneLabel: 'Assertions created',
  doneWithStats: (stats: SwaggerImportStats) => formatAssertionsImportLabel(stats),
} as const;

/** Full wizard (legacy) — includes assertions. */
export const SWAGGER_IMPORT_STEP_BLUEPRINT = [
  STEP_ENVIRONMENT,
  STEP_COLLECTIONS,
  STEP_DATASETS,
  STEP_ASSERTIONS,
] as const;

/** AI Agent import — full four-step UI; assertions are generated during post-import (same as Swagger wizard). */
export const AGENT_SWAGGER_IMPORT_STEP_BLUEPRINT = SWAGGER_IMPORT_STEP_BLUEPRINT;

export function environmentStepLabel(outcome?: SwaggerImportEnvironmentOutcome): string {
  switch (outcome) {
    case 'reused':
      return 'Using existing environment';
    case 'skipped_plan_limit':
      return 'Environment not created (plan limit reached)';
    case 'skipped':
      return 'Environment not created';
    case 'created':
    default:
      return 'Environment created successfully';
  }
}

export function environmentStepTone(
  outcome?: SwaggerImportEnvironmentOutcome,
): 'success' | 'warning' {
  return outcome === 'created' || outcome === 'reused' || outcome === undefined
    ? 'success'
    : 'warning';
}

export function createInitialSwaggerImportSteps(): AgentSwaggerImportStep[] {
  return SWAGGER_IMPORT_STEP_BLUEPRINT.map((step, index) => ({
    id: step.id,
    status: index === 0 ? 'running' : 'pending',
    label: step.runningLabel,
  }));
}

export function buildFinalSwaggerImportSteps(
  stats: SwaggerImportStats | null,
): AgentSwaggerImportStep[] {
  return SWAGGER_IMPORT_STEP_BLUEPRINT.map((step) => {
    if (step.id === 'environment') {
      const outcome = stats?.environmentOutcome ?? 'created';
      return {
        id: step.id,
        status: 'done' as const,
        label: environmentStepLabel(outcome),
        tone: environmentStepTone(outcome),
      };
    }
    return {
      id: step.id,
      status: 'done' as const,
      label: stats ? step.doneWithStats(stats) : step.doneLabel,
      tone: 'success' as const,
    };
  });
}

/**
 * Advances the running step to done and starts the next.
 * Uses provisional success labels; final import response replaces them with stats.
 */
export function advanceSwaggerImportSteps(
  steps: AgentSwaggerImportStep[],
): AgentSwaggerImportStep[] {
  const runningIndex = steps.findIndex((step) => step.status === 'running');
  if (runningIndex === -1 || runningIndex >= steps.length - 1) {
    return steps;
  }
  const blueprint = SWAGGER_IMPORT_STEP_BLUEPRINT;
  const currentBlueprint = blueprint[runningIndex];
  const nextBlueprint = blueprint[runningIndex + 1];
  return steps.map((step, index) => {
    if (index === runningIndex) {
      return {
        ...step,
        status: 'done',
        label: currentBlueprint.doneLabel,
        tone: 'success',
      };
    }
    if (index === runningIndex + 1) {
      return {
        ...step,
        status: 'running',
        label: nextBlueprint.runningLabel,
      };
    }
    return step;
  });
}

function liveCount(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
}

function plural(count: number, singular: string, pluralForm: string): string {
  return count === 1 ? singular : pluralForm;
}

/**
 * Drive step status from backend job percent so the UI cannot jump ahead of real work.
 * 0–19 environment, 20–54 collections, 55–74 data sets, 75–94 assertions, 95+ wrapping up.
 */
export function swaggerImportStepsFromJobProgress(
  percent: number,
  counts?: SwaggerImportLiveCounts,
): AgentSwaggerImportStep[] {
  const collections = liveCount(counts?.collectionsCreated);
  const requests = liveCount(counts?.requestsCreated);
  const dataSets = liveCount(counts?.dataSetsCreated);
  const assertions = liveCount(counts?.assertionsCreated);
  const safePercent = Number.isFinite(percent) ? percent : 0;

  const envDone = safePercent >= 20;
  const collectionsDone = safePercent >= 55;
  const datasetsDone = safePercent >= 75;
  const assertionsDone = safePercent >= 95;

  const collectionsRunning = safePercent >= 20 && !collectionsDone;
  const datasetsRunning = safePercent >= 55 && !datasetsDone;
  const assertionsRunning = safePercent >= 75 && !assertionsDone;

  return [
    {
      id: STEP_ENVIRONMENT.id,
      status: envDone ? 'done' : 'running',
      label: envDone ? STEP_ENVIRONMENT.doneLabel : STEP_ENVIRONMENT.runningLabel,
      tone: envDone ? 'success' : undefined,
    },
    {
      id: STEP_COLLECTIONS.id,
      status: collectionsDone ? 'done' : collectionsRunning ? 'running' : 'pending',
      label: collectionsDone
        ? `Imported ${collections} ${plural(collections, 'collection', 'collections')} and ${requests} ${plural(requests, 'request', 'requests')}`
        : collectionsRunning
          ? `Created ${collections} ${plural(collections, 'collection', 'collections')}, ${requests} ${plural(requests, 'request', 'requests')}…`
          : STEP_COLLECTIONS.runningLabel,
      tone: collectionsDone ? 'success' : undefined,
    },
    {
      id: STEP_DATASETS.id,
      status: datasetsDone ? 'done' : datasetsRunning ? 'running' : 'pending',
      label: datasetsDone
        ? formatDataSetsImportLabel({ collections, requests, dataSets, assertions })
        : datasetsRunning
          ? `Created ${dataSets} ${plural(dataSets, 'data set', 'data sets')}…`
          : STEP_DATASETS.runningLabel,
      tone: datasetsDone ? 'success' : undefined,
    },
    {
      id: STEP_ASSERTIONS.id,
      status: assertionsDone ? 'done' : assertionsRunning ? 'running' : 'pending',
      label: assertionsDone
        ? formatAssertionsImportLabel({ collections, requests, dataSets, assertions })
        : assertionsRunning
          ? `Created ${assertions} ${plural(assertions, 'assertion', 'assertions')}…`
          : STEP_ASSERTIONS.runningLabel,
      tone: assertionsDone ? 'success' : undefined,
    },
  ];
}
